const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "http://localhost:8787";

async function request(path, { signal } = {}) {
	const resp = await fetch(`${WORKER_URL}${path}`, { signal });
	if (!resp.ok) {
		let message = `Request failed: ${resp.status}`;
		try {
			const body = await resp.json();
			if (body?.error) message = body.error;
		} catch {
			// non-JSON body, keep default message
		}
		const err = new Error(message);
		err.status = resp.status;
		throw err;
	}
	return resp.json();
}

export function searchGames(query, { limit = 20, signal } = {}) {
	const params = new URLSearchParams({ q: query, limit: String(limit) });
	return request(`/search?${params}`, { signal });
}

export function getGame(igdbId, { signal } = {}) {
	return request(`/game/${igdbId}`, { signal });
}

// Search game SERIES (IGDB collections) by name. Returns
// [{ collection_id, title, game_count }].
export function searchGameSeries(query, { signal } = {}) {
	const params = new URLSearchParams({ q: query });
	return request(`/games/series?${params}`, { signal });
}

// All games in a collection, ordered by release date. Returns
// { collection_id, title, games: [<formatGame>…] }.
export function getGameSeries(collectionId, { signal } = {}) {
	return request(`/games/series/${collectionId}`, { signal });
}

// The series a specific game belongs to + its other entries (for the "More in
// this series" section on the game detail screen). Returns
// { collection_id, title, games } — collection_id null if the game has no series.
export function getGameSeriesForGame(igdbId, { signal } = {}) {
	return request(`/game/${igdbId}/series`, { signal });
}

// A game's editions (Standard / Deluxe / GOTY …), sourced from IGDB version
// records. Returns { size, next_offset, editions }. size is null for games
// (IGDB has no cheap total); paginate via next_offset until it's null.
export function getGameEditions(igdbId, { limit, offset, signal } = {}) {
	const params = new URLSearchParams();
	if (limit != null) params.set("limit", String(limit));
	if (offset != null) params.set("offset", String(offset));
	const qs = params.toString();
	return request(`/game/${igdbId}/editions${qs ? `?${qs}` : ""}`, { signal });
}

// Genres and platforms barely ever change, so cache the lists for the session
// to avoid hitting the worker (and IGDB rate limits) when the user flips tabs.
let genresPromise = null;
let platformsPromise = null;

export function browseGenres({ signal } = {}) {
	if (!genresPromise) {
		genresPromise = request("/browse/genres", { signal }).catch((err) => {
			genresPromise = null;
			throw err;
		});
	}
	return genresPromise;
}

export function browsePlatforms({ signal } = {}) {
	if (!platformsPromise) {
		platformsPromise = request("/browse/platforms", { signal }).catch(
			(err) => {
				platformsPromise = null;
				throw err;
			},
		);
	}
	return platformsPromise;
}

export function browseGames(
	{ letter, year, decade, platform, genre, sort, limit, offset } = {},
	{ signal } = {},
) {
	const params = new URLSearchParams();
	if (letter) params.set("letter", letter);
	if (year != null) params.set("year", String(year));
	if (decade) params.set("decade", String(decade));
	if (platform != null) params.set("platform", String(platform));
	if (genre != null) params.set("genre", String(genre));
	if (sort) params.set("sort", sort);
	if (limit != null) params.set("limit", String(limit));
	if (offset != null) params.set("offset", String(offset));
	const qs = params.toString();
	return request(`/browse/games${qs ? `?${qs}` : ""}`, { signal });
}

// Store / platform links for a game: [{ label, url }] (Official site, Steam, GOG…).
export function getGameLinks(igdbId, { signal } = {}) {
	return request(`/game/${igdbId}/links`, { signal });
}
